import { Button } from "@/components/ui/button";
import { Shield, Users, CheckCircle, ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

const FinalCTA = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  const scrollToSection = (selector: string) => {
    document.querySelector(selector)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-primary/5 via-background to-accent/10 relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="absolute top-10 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-pulse-soft animation-delay-400" />

      <div className="container-custom relative" ref={ref}>
        <div className={cn(
          "max-w-4xl mx-auto text-center transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Ready to Take Control of Your{" "}
            <span className="text-gradient">Credit?</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
            Choose the option that fits your situation and start disputing credit
            report errors today.
          </p>

          <div className={cn(
            "grid sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-10 stagger-children",
            isVisible && "visible"
          )}>
            <Button
              variant="gold"
              size="lg"
              className="w-full btn-glow-animated group px-4 sm:px-6 lg:px-8 whitespace-normal h-auto min-h-12 py-3"
              onClick={() => scrollToSection("#kit-section")}
            >
              Complete DIY Kit — $99
              <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="w-full hover:border-primary/50 transition-colors group px-4 sm:px-6 lg:px-8 whitespace-normal h-auto min-h-12 py-3"
              onClick={() => scrollToSection("#templates-section")}
            >
              Single Templates — $29.99
            </Button>
          </div>

          {/* Trust badges with stagger */}
          <div className={cn(
            "flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground",
            isVisible && "stagger-children visible"
          )}>
            {[
              { icon: Shield, label: "FCRA Protected" },
              { icon: Users, label: "10K+ DIY Users" },
              { icon: CheckCircle, label: "Instant Access" },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 group cursor-default hover-icon-pop">
                <item.icon className="w-5 h-5 text-primary icon-target" />
                <span className="group-hover:text-foreground transition-colors">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
